/**
 * スタッフレコードの整形（純関数）。
 *
 * - buildStaffInsert        … 直接 DB 挿入用 insert（role は持たない）
 * - buildCreateStaffUserBody … create-staff-user Edge Function の body（role を含む）
 * - buildStaffPatch         … 更新用 patch
 */
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { CreateStaffUserBody } from "@/domain/repositoryInterface/StaffGateway";

type StaffStatus = NonNullable<Tables<"staff">["status"]>;

export interface CreateStaffValues {
  staff_name: string;
  phone?: string;
  email?: string;
  status?: string;
  allocate_order?: number;
  max_staff_count?: number;
  sort_order?: number;
  login_id?: string;
  password?: string;
  role?: string;
}

export interface UpdateStaffValues {
  staff_name: string;
  phone?: string;
  email?: string;
  status?: string;
  allocate_order?: number;
  max_staff_count?: number;
  sort_order?: number;
  hpb_id?: string;
}

/** 直接 DB 挿入用 insert（認証ユーザーを作らない経路）。 */
export function buildStaffInsert(
  values: CreateStaffValues,
  shopId: string,
): TablesInsert<"staff"> {
  return {
    staff_name: values.staff_name,
    phone: values.phone || null,
    email: values.email || null,
    status: (values.status || "active") as StaffStatus,
    allocate_order: values.allocate_order ?? null,
    max_staff_count: values.max_staff_count ?? null,
    sort_order: values.sort_order ?? null,
    shop_id: shopId,
  } as TablesInsert<"staff">;
}

/** create-staff-user Edge Function 用 body（login_id/password は呼び出し側で保証）。 */
export function buildCreateStaffUserBody(
  values: CreateStaffValues & { login_id: string; password: string },
  shopId: string,
): CreateStaffUserBody {
  return {
    login_id: values.login_id,
    password: values.password,
    staff_name: values.staff_name,
    phone: values.phone || null,
    email: values.email || null,
    status: values.status || "active",
    allocate_order: values.allocate_order ?? null,
    max_staff_count: values.max_staff_count ?? null,
    sort_order: values.sort_order ?? null,
    role: values.role || "user",
    shop_id: shopId,
  };
}

/** 更新用 patch（staff_name と allocate/max/sort は常に設定、その他は指定時のみ）。 */
export function buildStaffPatch(values: UpdateStaffValues): TablesUpdate<"staff"> {
  const patch: TablesUpdate<"staff"> = {
    staff_name: values.staff_name,
    allocate_order: values.allocate_order ?? null,
    max_staff_count: values.max_staff_count ?? null,
    sort_order: values.sort_order ?? null,
  };
  if (values.phone !== undefined) patch.phone = values.phone || null;
  if (values.email !== undefined) patch.email = values.email || null;
  if (values.status !== undefined) patch.status = (values.status || "active") as StaffStatus;
  if (values.hpb_id !== undefined) patch.hpb_id = values.hpb_id || null;
  return patch;
}
